






































































import type { PropType } from '@nuxtjs/composition-api';
// eslint-disable-next-line no-duplicate-imports
import {
  computed,
  defineComponent,
  inject,
  onMounted,
  ref,
  useContext,
  watch
} from '@nuxtjs/composition-api';
import { SfLoader } from '@storefront-ui/vue';
import VaimoProductCard from 'organisms/VaimoProductCard.vue';

import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import { useImage, useProduct } from '~/composables';
import VaimoGrid from '~/diptyqueTheme/components/molecules/VaimoGrid.vue';
import VaimoSlider from '~/diptyqueTheme/components/molecules/VaimoSlider.vue';
import {
  useAlternativeVisual,
  useGoogleTagManager,
  usePreviewMode,
  useRequestPath
} from '~/diptyqueTheme/composable';
import groupTypes from '~/diptyqueTheme/config/productGroupTypes';
import {
  addRefParamToUrl,
  loadRecommendations,
  reportAddToCart
} from '~/diptyqueTheme/helpers/nosto';
import customProductGetters from '~/diptyqueTheme/helpers/productGetters';
import { useNostoStore } from '~/diptyqueTheme/stores/nosto';
import type {
  DescriptionOption,
  Visual
} from '~/diptyqueTheme/types/contentful';
import { DESC_SETTING } from '~/diptyqueTheme/types/contentful/constants/injectionKeys.const';
import { useAddToCart } from '~/helpers/cart/addToCart';
import { Logger } from '~/helpers/logger';
import productGetters from '~/modules/catalog/product/getters/productGetters';
import type { Product } from '~/modules/catalog/product/types';

export default defineComponent({
  name: 'VaimoProducts',
  components: {
    SkeletonLoader,
    VaimoProductCard,
    VaimoSlider,
    VaimoGrid,
    SfLoader
  },
  props: {
    uniqueKey: {
      required: false,
      type: String,
      default: 'VaimoProducts'
    },
    skuList: {
      required: false,
      type: Array as PropType<string[]>,
      default: () => []
    },
    skuListUnsorted: {
      required: false,
      type: Array,
      default: () => []
    },
    skuListUnsortedFull: {
      required: false,
      type: Array,
      default: () => []
    },
    skuListType: {
      required: false,
      type: String,
      default: 'listOfSku'
    },
    nostoId: {
      required: false,
      type: String,
      default: ''
    },
    display: {
      required: false,
      type: String,
      default: 'slider',
      validator: (val: string) => ['slider', 'grid'].includes(val)
    },
    wrapperProps: {
      required: false,
      type: Object,
      default: () => ({})
    },
    pageSize: {
      type: Number,
      required: false,
      default: 20
    },
    loadingType: {
      required: false,
      type: String,
      default: 'spinner',
      validator: (val: string) => ['spinner', 'skeleton', ''].includes(val)
    },
    gridColsMobile: {
      type: Number,
      required: false,
      default: 2
    },
    gridColsDesktop: {
      type: Number,
      required: false,
      default: 4
    },
    isCollection: {
      type: Boolean,
      required: false,
      default: false
    },
    altVisuals: {
      type: Array as PropType<Visual[]>,
      required: false,
      default: () => [] as Visual[]
    },
    getProductsByCategory: Boolean,
    categoryData: {
      required: false,
      type: Object,
      default: () => ({})
    }
  },
  emits: ['productsLoaded'],
  setup(props, { emit }) {
    const { app } = useContext();
    const { getViewItemListDetails, getSelectItemDetails } =
      useGoogleTagManager();
    const { getMagentoImage } = useImage();
    const { getSliderProductsList, loading: productsLoading } = useProduct();
    const { getRelatedProductUrl } = useRequestPath();
    const { applyAltVisuals } = useAlternativeVisual();
    const { isPreviewMode } = usePreviewMode();
    const { getClientScriptLoaded } = useNostoStore();

    const SKUsUnsorted = computed(() => props.skuListUnsorted);
    const SKUsUnsortedFull = computed(() => props.skuListUnsortedFull);
    const SKUs = computed(() => props.skuList);
    const SKUsType = computed(() => props.skuListType);
    const products = ref([]);
    const { isInCart } = useAddToCart();

    const hideDescriptionOptions = inject<DescriptionOption[]>(
      DESC_SETTING,
      []
    );

    const getProducts = async () => {
      emit('productsLoaded', false);
      const loadedSKUs = products.value.map((p) => p.sku).sort();

      if (
        props.getProductsByCategory &&
        Object.keys(props.categoryData)?.length
      ) {
        await getProductsByCategoryID();
      }

      if (Array.isArray(SKUs.value)) {
        if (loadedSKUs[0] === SKUs.value[0]) return;
      }

      if (SKUsType.value === groupTypes.NOSTO) {
        try {
          const recommendedIds = await loadRecommendations(props.nostoId);

          if (recommendedIds?.length) {
            await getProductsBySku(recommendedIds, SKUsType.value);
          }
        } catch (error) {
          Logger.info('Failed to load recommendations:', error, props.nostoId);
        }
      } else if (SKUs.value?.length && !props.getProductsByCategory) {
        await getProductsBySku(SKUs.value, SKUsType.value);
      }

      emit('productsLoaded', true);

      if (products.value.length) {
        app.$gtm.push(getViewItemListDetails(products.value, 'slider'));
      }
    };

    const getProductsByCategoryID = async () => {
      try {
        const data = await getSliderProductsList({
          filter: {
            category_id: {
              eq: props.categoryData.categoryID
            }
          },
          pageSize: props.categoryData.limit,
          is_preview: isPreviewMode.value
        });
        products.value = data?.items;
      } catch (e) {
        Logger.error('getProductsByCategoryID', e);
      }
    };

    const getProductsBySku = async (sku_array: string[], type: string) => {
      const filterType = type === groupTypes.NOSTO ? '_id' : 'sku';
      try {
        const data = await getSliderProductsList({
          filter: {
            [filterType]: {
              in: sku_array
            }
          },
          pageSize: props.pageSize,
          is_preview: isPreviewMode.value
        });

        const result = data?.items;

        if (props.isCollection) {
          if (SKUsUnsortedFull.value.length) {
            result.sort(function (a, b) {
              return (
                SKUsUnsortedFull.value.indexOf(a.sku) -
                SKUsUnsortedFull.value.indexOf(b.sku)
              );
            });
          } else {
            result.sort(function (a, b) {
              return (
                SKUsUnsorted.value.indexOf(a.sku) -
                SKUsUnsorted.value.indexOf(b.sku)
              );
            });
          }
        } else {
          const sortElem = type === groupTypes.NOSTO ? 'id' : 'sku';
          result?.sort((a, b) => {
            return (
              sku_array.indexOf(String(a[sortElem])) -
              sku_array.indexOf(String(b[sortElem]))
            );
          });
        }
        products.value = applyAltVisuals(result as Product[], props.altVisuals);
      } catch (e) {
        Logger.error('getProductsBySku', e);
      }
    };

    watch(SKUs, async () => {
      await getProducts();
    });

    const isNostoSlider = computed(() => SKUsType.value === groupTypes.NOSTO);
    const hasNoProducts = computed(() => !products.value.length);

    onMounted(async () => {
      await getProducts();

      // temporary solution for reloading nosto products
      if (isNostoSlider.value && hasNoProducts.value) {
        setTimeout(getProducts, 500);
      }
    });

    const onGtmSelectItem = (product: Product, index: number, event): void => {
      // Prevents GTM event from firing when clicking on the add to cart button
      if (event.target.closest('.vaimo-product-card__add-to-cart')) {
        return;
      }

      app.$gtm.push(getSelectItemDetails(product, index, 'slider'));
    };

    const getProductLink = (product: Product): string => {
      let link = app.localePath(getRelatedProductUrl(product));

      if (props.nostoId) {
        link = addRefParamToUrl(link, props.nostoId);
      }

      return link;
    };

    const handleClickOnProduct = (product: Product, index: number, event) => {
      onGtmSelectItem(product, index + 1, event);
    };

    const wrapperComponent = computed(() => {
      if (props.display === 'slider') return 'VaimoSlider';
      if (props.display === 'grid') return 'VaimoGrid';
    });

    const wrapperDisplayProps = computed(() => {
      return props.wrapperProps[props.display] || {};
    });

    const itemsLength = computed(() => {
      return products.value?.length || 0;
    });

    const wrapperClasses = computed(() => {
      if (props.display === 'grid') {
        return [
          'grid-cols-' + props.gridColsMobile,
          'm-and-l:grid-cols-' + props.gridColsDesktop
        ];
      }
      return '';
    });

    const sendNostoAddToCartEvent = (id: string) => {
      if (!props.nostoId || !getClientScriptLoaded()) return;

      reportAddToCart(String(id), props.nostoId);
    };

    return {
      productGetters,
      customProductGetters,
      products,
      productsLoading,
      getProductLink,
      getMagentoImage,
      handleClickOnProduct,
      wrapperComponent,
      wrapperDisplayProps,
      wrapperClasses,
      itemsLength,
      sendNostoAddToCartEvent,
      isInCart,
      hideDescriptionOptions
    };
  }
});
